import { ReactNode } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { formatNumber } from "../format";

export interface VBarItem {
  key: string;
  /** compact x-axis label (truncated with ellipsis if it doesn't fit) */
  label: string;
  /** full label shown as a native tooltip on the axis label */
  title?: string;
  /** numeric value used for bar height scaling; null draws no bar */
  value: number | null;
  /** bar color (defaults to green) */
  color?: string;
  /** rich hover content */
  tooltip?: ReactNode;
}

interface Props {
  items: VBarItem[];
  /** Y-axis unit caption, e.g. "tCO₂e". */
  unit: string;
  defaultColor?: string;
}

/*
 * Geometry from Figma "Chart/Bar/Vertical" 7150:20415 (dashboard Emissions by
 * Sector, section 7053:14433). Values are px.
 *
 * Y axis (7150:19228): a 24px unit caption (14 medium #818181), then six 48px
 * tick rows (24px label + 12/12 padding) — gridlines sit 48 apart and 0..max
 * spans 5 * 48 = 240. Labels are 50 wide, right-aligned (14 regular #818181),
 * after 10px padding; the 0.5px #dedede 4/4-dashed gridline starts 10px later at
 * x = 70 and stops 10px short of the right edge. An L-shaped #dedede axis
 * (7150:20414, left + bottom rule) frames the 240px plot.
 *
 * Bars (7150:19511): a column block 7.5px from the top, 316 tall = 280 bar area
 * + 12 gap + 24 label, so bars end on the zero line (7.5 + 280 = 287.5). Each
 * column has 16px side padding around a square-topped bar; its label is 14
 * medium #818181, centred, ellipsised, inside 4px side padding. Heights scale to
 * the AXIS maximum, not to the tallest bar.
 */
const TICKS = 6;
const UNIT_ROW = 24;
const TICK_ROW = 48;
const PLOT = (TICKS - 1) * TICK_ROW;
const LABEL_LEFT = 10;
const LABEL_WIDTH = 50;
const GUTTER = 70;
const RIGHT_PAD = 10;
const BARS_TOP = 7.5;
const BAR_AREA = 280;
const X_LABEL_GAP = 12;
const X_LABEL_HEIGHT = 24;
const BARS_RIGHT = 12; // 1230 - 70 - 1148
const COL_PAD = 16;
/** Figma's column: 1148 / 12 = 95.67 (63.67 bar + 16 + 16). Columns keep that
 *  width when there are fewer sectors, so the bar width and the 32px gap between
 *  bars match the design instead of stretching across the card; more sectors
 *  (See More) share the row and narrow. */
const COLUMN_WIDTH = 1148 / 12;
/** 7.5 + 280 + 12 + 24 = Figma's 323.5px chart frame. */
const HEIGHT = BARS_TOP + BAR_AREA + X_LABEL_GAP + X_LABEL_HEIGHT;

/** Smallest axis maximum >= `max` that splits into `steps` round steps. */
export const niceMax = (max: number, steps: number): number => {
  if (max <= 0) return steps;
  const magnitude = Math.pow(10, Math.floor(Math.log10(max / steps)));
  for (const multiple of [1, 2, 2.5, 5, 10]) {
    const step = magnitude * multiple;
    if (step * steps >= max) return step * steps;
  }
  return Math.ceil(max / steps) * steps;
};

/** Vertical bar (column) chart — Figma Chart/Bar/Vertical 7150:17982. Built from
 *  divs (not MUI x-charts) so the tooltip can show multi-line detail. */
const VBarChart = ({ items, unit, defaultColor = "var(--green)" }: Props) => {
  const steps = TICKS - 1;
  const axisMax = niceMax(Math.max(0, ...items.map((i) => i.value ?? 0)), steps);

  return (
    <Box position="relative" height={`${HEIGHT}px`} width="100%">
      {/* Unit caption (Figma 7150:19231) */}
      <Typography
        position="absolute"
        top={0}
        left={0}
        width={`${LABEL_WIDTH}px`}
        textAlign="right"
        fontSize="14px"
        fontWeight={500}
        lineHeight="24px"
        color="var(--gray1)"
        whiteSpace="nowrap"
      >
        {unit}
      </Typography>

      {/* Tick labels + dashed gridlines, top (max) to bottom (0) */}
      {Array.from({ length: TICKS }, (_, i) => {
        const rowTop = UNIT_ROW + i * TICK_ROW;
        return (
          <Box key={i}>
            <Typography
              position="absolute"
              top={`${rowTop + (TICK_ROW - 24) / 2}px`}
              left={`${LABEL_LEFT}px`}
              width={`${LABEL_WIDTH}px`}
              textAlign="right"
              fontSize="14px"
              fontWeight={400}
              lineHeight="24px"
              color="var(--gray1)"
              whiteSpace="nowrap"
            >
              {formatNumber((axisMax * (steps - i)) / steps)}
            </Typography>
            <Box
              position="absolute"
              top={`${rowTop + TICK_ROW / 2 - 0.25}px`}
              left={`${GUTTER}px`}
              right={`${RIGHT_PAD}px`}
              height="0.5px"
              sx={{
                // Figma: 0.5px stroke, #dedede, 4/4 dash
                backgroundImage:
                  "repeating-linear-gradient(to right, var(--gray2) 0 4px, transparent 4px 8px)",
              }}
            />
          </Box>
        );
      })}

      {/* L-shaped axis framing the plot (Figma 7150:20414) */}
      <Box
        position="absolute"
        top={`${UNIT_ROW + TICK_ROW / 2 + 0.5}px`}
        left={`${GUTTER}px`}
        right={`${RIGHT_PAD}px`}
        height={`${PLOT}px`}
        boxSizing="border-box"
        sx={{
          borderLeft: "1px solid var(--gray2)",
          borderBottom: "1px solid var(--gray2)",
          pointerEvents: "none",
        }}
      />

      {/* Columns: bar area + x label (Figma 7150:19511) */}
      <Box
        position="absolute"
        top={`${BARS_TOP}px`}
        left={`${GUTTER}px`}
        right={`${BARS_RIGHT}px`}
        height={`${BAR_AREA + X_LABEL_GAP + X_LABEL_HEIGHT}px`}
        display="flex"
      >
        {items.map((it) => (
          <Box
            key={it.key}
            flex={`0 1 ${COLUMN_WIDTH}px`}
            minWidth={0}
            display="flex"
            flexDirection="column"
            gap={`${X_LABEL_GAP}px`}
          >
            <Tooltip
              arrow
              placement="top"
              title={it.tooltip ?? ""}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "var(--dark)",
                    maxWidth: 300,
                    p: "10px 12px",
                    borderRadius: "8px",
                  },
                },
                arrow: { sx: { color: "var(--dark)" } },
              }}
            >
              <Box
                height={`${BAR_AREA}px`}
                px={`${COL_PAD}px`}
                display="flex"
                alignItems="flex-end"
                sx={{ cursor: "default" }}
              >
                {/* an unknown value draws no bar; the tooltip still says why */}
                {it.value != null && (
                  <Box
                    width="100%"
                    height={`${(Math.max(it.value, 0) / axisMax) * PLOT}px`}
                    bgcolor={it.color ?? defaultColor}
                    sx={{ "&:hover": { opacity: 0.85 } }}
                  />
                )}
              </Box>
            </Tooltip>
            <Box px="4px" minWidth={0}>
              <Typography
                fontSize="14px"
                fontWeight={500}
                lineHeight="24px"
                color="var(--gray1)"
                textAlign="center"
                title={it.title ?? it.label}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {it.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default VBarChart;
